import { container } from '@web/services/container';

import { type VehicleDetailsEntity, FetchVehicleDetails, ValidateVin } from '@packages/domain/techline/vin';

import { useCallback, useState } from 'react';
import { useAbortController } from '@packages/core/http';

const [$fetchVehicleDetails, $validateVin] = container.resolveOperations(FetchVehicleDetails, ValidateVin);

const MIN_VIN_LENGTH = 8;
const MAX_VIN_LENGTH = 17;
const ALLOWED_VIN_CHARACTERS = /^[A-HJ-NPR-Z0-9]+$/i;

function isVinFormatValid(vin: string): boolean {
    const trimmed = vin.trim();
    const isValidLength = trimmed.length === MIN_VIN_LENGTH || trimmed.length === MAX_VIN_LENGTH;
    return isValidLength && ALLOWED_VIN_CHARACTERS.test(trimmed);
}

export function useVinSearch() {
    const { abortSignalRef } = useAbortController();
    const [vinInputValue, setVinInputValue] = useState('');
    const [isVinCheckLoading, setIsVinCheckLoading] = useState(false);
    const [possibleVehicles, setPossibleVehicles] = useState<VehicleDetailsEntity[]>([]);
    const [vehicleDetails, setVehicleDetails] = useState<VehicleDetailsEntity | undefined>(undefined);
    const [vinError, setVinError] = useState('');

    const validateVin = useCallback(async () => {
        if (isVinCheckLoading || !vinInputValue) {
            return;
        }

        if (!isVinFormatValid(vinInputValue)) {
            setVinError('Invalid VIN format.');
            return;
        }

        setIsVinCheckLoading(true);
        setPossibleVehicles([]);
        setVehicleDetails(undefined);
        setVinError('');
        const isValid = await $validateVin({ vin: vinInputValue }, abortSignalRef.current);

        if (!isValid) {
            setVinError('No VIN found!');
            setIsVinCheckLoading(false);
            return;
        }

        const vehicleResults = await $fetchVehicleDetails({ vin: vinInputValue }, abortSignalRef.current);

        if (!vehicleResults) {
            setVinError('Error fetching vehicle details. Please refresh and try again.');
            setIsVinCheckLoading(false);
            return;
        }

        if (vehicleResults.length > 1) {
            setPossibleVehicles(vehicleResults);
        } else if (vehicleResults.length === 1) {
            setVehicleDetails(vehicleResults[0]);
        } else {
            setVinError('No VIN found. Please check the VIN and try again.');
        }

        setIsVinCheckLoading(false);
    }, [vinInputValue, isVinCheckLoading, abortSignalRef]);

    const selectVehicle = useCallback((vehicle: VehicleDetailsEntity) => {
        setPossibleVehicles([]);
        setVehicleDetails(vehicle);
        setVinInputValue(vehicle.vin);
    }, []);

    const resetVinSearch = useCallback(() => {
        setVinInputValue('');
        setIsVinCheckLoading(false);
        setPossibleVehicles([]);
        setVehicleDetails(undefined);
        setVinError('');
    }, []);

    return {
        validateVin,
        isVinCheckLoading,
        possibleVehicles,
        resetVinSearch,
        selectVehicle,
        setVehicleDetails,
        setVinInputValue,
        vehicleDetails,
        vinError,
        vinInputValue,
    };
}
